import axios from "axios";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2, RotateCcw, Eye, Download, Landmark } from "lucide-react";
import { API } from "./shared";

const SHOW_KEY = "gpp_admin_show_test";
const cell = { padding: "7px 10px", borderBottom: "1px solid #e5e7eb", fontSize: 12.5, textAlign: "left" };

axios.interceptors.request.use((cfg) => {
  if ((cfg.url || "").endsWith("/auth/logout")) localStorage.removeItem(SHOW_KEY);
  if (localStorage.getItem(SHOW_KEY) === "1" && (cfg.url || "").startsWith(API) && (cfg.method || "get") === "get") {
    cfg.params = { include_test: true, ...(cfg.params || {}) };
  }
  return cfg;
});

export const ShowTestDataToggle = () => {
  const [on, setOn] = useState(localStorage.getItem(SHOW_KEY) === "1");
  const flip = (v) => { localStorage.setItem(SHOW_KEY, v ? "1" : "0"); setOn(v); toast.success(v ? "Test data now visible in admin lists" : "Test data hidden from admin lists"); };
  return (
    <label data-testid="show-test-data-toggle" style={{ display: "inline-flex", gap: 6, alignItems: "center", fontSize: 12, fontWeight: 600, color: on ? "#B45309" : "#64748b", cursor: "pointer" }}>
      <input data-testid="show-test-data-input" type="checkbox" checked={on} onChange={e => flip(e.target.checked)} style={{ width: "auto" }}/>
      <Eye size={13}/> Show test-account data in admin lists
    </label>
  );
};

function PickList({ title, rows, label, excluded, onToggle, testid }) {
  if (!rows.length) return null;
  return (
    <details data-testid={testid} style={{ border: "1px solid #e5e7eb", borderRadius: 8, padding: "8px 12px" }}>
      <summary style={{ cursor: "pointer", fontWeight: 700, fontSize: 13 }}>{title} ({rows.length - rows.filter(r => excluded.has(r.id)).length} of {rows.length} selected)</summary>
      <div style={{ maxHeight: 260, overflow: "auto", marginTop: 8 }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}><tbody>
          {rows.map(r => (
            <tr key={r.id} data-testid={`${testid}-row-${r.id}`}>
              <td style={{ ...cell, width: 28 }}><input type="checkbox" data-testid={`${testid}-check-${r.id}`} checked={!excluded.has(r.id)} onChange={() => onToggle(r.id)} style={{ width: "auto" }}/></td>
              <td style={cell}>{label(r)}</td>
            </tr>))}
        </tbody></table>
      </div>
    </details>
  );
}

function Batches({ batches, onRestore }) {
  const [ask, setAsk] = useState(null);
  if (!batches.length) return null;
  return (
    <div data-testid="cleanup-batches">
      <b style={{ fontSize: 13 }}>Previous cleanups</b>
      <table style={{ width: "100%", borderCollapse: "collapse", marginTop: 6 }}>
        <thead><tr style={{ background: "#f3f4f6" }}>{["When", "Type", "By", "Records", "Archive", ""].map(h => <th key={h} style={cell}>{h}</th>)}</tr></thead>
        <tbody>{batches.map(b => (
          <tr key={b.id} data-testid={`cleanup-batch-${b.id}`}>
            <td style={cell}>{new Date(b.at).toLocaleString("en-IN")}</td>
            <td style={cell}>{b.kind === "financial_reset" ? "Financial reset" : "Test data cleanup"}</td>
            <td style={cell}>{b.by}</td>
            <td style={cell}>{b.total}</td>
            <td style={cell}>{b.restored_at ? `Restored ${new Date(b.restored_at).toLocaleDateString("en-IN")}` : b.restorable ? `Until ${new Date(b.expires_at).toLocaleDateString("en-IN")}` : "Expired"}</td>
            <td style={{ ...cell, whiteSpace: "nowrap" }}>{b.restorable && (ask === b.id ? (
              <span style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
                Restore {b.total} records?
                <button className="m-cta" data-testid={`cleanup-restore-confirm-${b.id}`} onClick={() => { setAsk(null); onRestore(b); }}>Yes</button>
                <button className="m-ghost" data-testid={`cleanup-restore-cancel-${b.id}`} onClick={() => setAsk(null)}>No</button>
              </span>
            ) : (
              <button className="m-ghost" data-testid={`cleanup-restore-${b.id}`} onClick={() => setAsk(b.id)} style={{ display: "inline-flex", alignItems: "center", gap: 5, whiteSpace: "nowrap" }}><RotateCcw size={12}/> Restore</button>
            ))}</td>
          </tr>))}
        </tbody>
      </table>
    </div>
  );
}

function ZohoList({ refs, testid }) {
  if (!refs?.length) return <p style={{ fontSize: 12, color: "#64748b", margin: 0 }} data-testid={`${testid}-none`}>No Zoho Books documents are linked to this test data.</p>;
  return (
    <div data-testid={testid} style={{ border: "1px solid #fde68a", background: "#fffbeb", borderRadius: 8, padding: 10 }}>
      <b style={{ fontSize: 13 }}>Void these in Zoho Books yourself ({refs.length})</b>
      <table style={{ width: "100%", borderCollapse: "collapse", marginTop: 6 }}><tbody>
        {refs.map(r => <tr key={r.zoho_id}><td style={cell}>{r.kind}</td><td style={cell}><b>{r.number}</b></td><td style={cell}>Zoho ID {r.zoho_id}</td><td style={cell}>{r.order}</td></tr>)}
      </tbody></table>
    </div>
  );
}

function FinancialReset({ onDone }) {
  const [info, setInfo] = useState(null);
  const [backupId, setBackupId] = useState("");
  const [phrase, setPhrase] = useState("");
  const [busy, setBusy] = useState("");
  const [done, setDone] = useState(null);
  const load = () => axios.get(`${API}/admin/financial-reset/preview`).then(r => setInfo(r.data)).catch(() => setInfo(null));
  useEffect(() => { load(); }, []);
  const backup = async () => {
    setBusy("backup");
    try {
      const r = await axios.get(`${API}/admin/financial-reset/backup`, { responseType: "blob" });
      const name = (r.headers["content-disposition"] || "").match(/filename="(.+)"/)?.[1] || "goprintpack-financial-backup.zip";
      const a = document.createElement("a"); a.href = URL.createObjectURL(r.data); a.download = name; a.click(); URL.revokeObjectURL(a.href);
      setBackupId(r.headers["x-backup-id"] || ""); toast.success("Backup downloaded — keep this file safe"); load();
    } catch { toast.error("Backup failed"); } finally { setBusy(""); }
  };
  const reset = async () => {
    setBusy("reset");
    try {
      const r = await axios.post(`${API}/admin/financial-reset/reset`, { confirmation_phrase: phrase, backup_id: backupId });
      setDone(r.data); setPhrase(""); setBackupId(""); toast.success(`Financial data cleared (${r.data.total} records, archived 30 days)`); load(); onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Reset failed"); } finally { setBusy(""); }
  };
  if (!info) return null;
  const rows = Object.entries(info.records).filter(([, n]) => n);
  const links = Object.entries(info.linked_orders).filter(([, n]) => n);
  return (
    <div data-testid="fin-reset" style={{ border: "1px solid #fecaca", borderRadius: 10, padding: 14, display: "grid", gap: 10 }}>
      <b style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 14 }}><Landmark size={15}/> Financial reset: back up and clear all invoices & money records</b>
      <p style={{ fontSize: 12.5, color: "#64748b", margin: 0 }}>
        Clears invoices, settlements, credit/debit notes, vendor bills, payouts, payment & escrow records (real and test). Jobs, briefs and RFQs are kept, but their invoice/payment links are cleared.
        Invoice numbering continues from the last number used (no GST number is ever repeated). Everything is archived for 30 days and can be restored below.
      </p>
      <div data-testid="fin-reset-counts" style={{ fontSize: 12.5 }}>
        <b data-testid="fin-reset-total">{info.total}</b> financial records{rows.length ? `: ${rows.map(([c, n]) => `${c.replace(/_/g, " ")} ${n}`).join(" · ")}` : ""}
        {links.length > 0 && <> · orders with links: {links.map(([c, n]) => `${c.replace(/_/g, " ")} ${n}`).join(" · ")}</>}
      </div>
      {done && <div className="mat-ok" data-testid="fin-reset-done">Cleared {done.total} records. You're starting fresh.</div>}
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
        <button className="m-cta" data-testid="fin-backup-btn" disabled={!!busy} onClick={backup} style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
          <Download size={14}/> {busy === "backup" ? "Preparing…" : "1. Download backup (ZIP)"}
        </button>
        {info.last_backup && <span style={{ fontSize: 11.5, color: "#64748b" }} data-testid="fin-last-backup">Last backup {new Date(info.last_backup.at).toLocaleString("en-IN")} by {info.last_backup.by}</span>}
      </div>
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
        <input data-testid="fin-reset-phrase" value={phrase} onChange={e => setPhrase(e.target.value)} disabled={!backupId}
          placeholder={backupId ? `Type ${info.confirmation_phrase} to confirm` : "Download the backup first"}
          style={{ border: "1px solid #CBD5E1", borderRadius: 7, padding: "8px 10px", fontSize: 12.5, minWidth: 280 }}/>
        <button className="primary-btn" data-testid="fin-reset-btn" style={{ background: "#B91C1C" }}
          disabled={!backupId || !!busy || phrase.trim() !== info.confirmation_phrase} onClick={reset}>
          {busy === "reset" ? "Clearing…" : "2. Clear all financial data"}
        </button>
      </div>
    </div>
  );
}

export function AcDataCleanup() {
  const [data, setData] = useState(null);
  const [batches, setBatches] = useState([]);
  const [excluded, setExcluded] = useState(new Set());
  const [phrase, setPhrase] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const load = () => {
    axios.get(`${API}/admin/data-cleanup/preview`).then(r => setData(r.data)).catch(e => toast.error(e.response?.data?.detail || "Could not load preview"));
    axios.get(`${API}/admin/data-cleanup/batches`).then(r => setBatches(r.data.batches || [])).catch(() => {});
  };
  useEffect(load, []);
  const toggle = (id) => setExcluded(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const purge = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/admin/data-cleanup/purge`, { confirmation_phrase: phrase, exclude_ids: [...excluded] });
      setResult(r.data); setPhrase(""); setExcluded(new Set());
      toast.success(`Removed ${r.data.total} test records (archived for 30 days)`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Cleanup failed"); } finally { setBusy(false); }
  };
  const restore = async (b) => {
    try { const r = await axios.post(`${API}/admin/data-cleanup/batches/${b.id}/restore`); setResult(null); r.data.failed ? toast.warning(`Restored ${r.data.total}; ${r.data.failed} could not be restored — try again`) : toast.success(`Restored ${r.data.total} records`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Restore failed"); }
  };
  const del = data?.accounts.filter(a => a.action === "delete") || [];
  const keep = data?.accounts.filter(a => a.action === "keep_empty") || [];
  return (
    <div className="ac-panel" data-testid="ac-data-cleanup">
      <header className="ac-panel-head">
        <b style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><Trash2 size={15}/> Test data cleanup</b>
        <ShowTestDataToggle/>
      </header>
      <p style={{ fontSize: 12.5, color: "#64748b", margin: 0 }}>
        Removes everything created by or involving test/demo accounts, items with test-style names (TEST_, PyTest, "… Test") and throw-away accounts from automated tests.
        Demo logins are kept (emptied and hidden from real users). Everything removed is archived for 30 days and can be restored. Untick anything that is real.
      </p>
      {result && <div className="mat-ok" data-testid="cleanup-result">Cleanup done: {result.total} records removed. <ZohoList refs={result.zoho_refs} testid="cleanup-result-zoho"/></div>}
      {!data ? <div style={{ fontSize: 13 }}>Scanning…</div> : data.total_records === 0 ? (
        <div className="mat-ok" data-testid="cleanup-clean">No test data found. Your site shows real data only.</div>
      ) : (
        <>
          <div data-testid="cleanup-summary" style={{ fontSize: 13 }}>
            Found <b data-testid="cleanup-total">{data.total_records}</b> records: {data.jobs.length} jobs, {data.briefs.length} design briefs, {data.rfqs.length} sub-RFQs, {del.length} throw-away accounts
            {excluded.size > 0 && <b data-testid="cleanup-excluded-note" style={{ color: "#B45309" }}> · {excluded.size} item{excluded.size === 1 ? "" : "s"} unticked (they and their linked records will be kept)</b>}
            {" "}({Object.entries(data.counts).filter(([, n]) => n).map(([c, n]) => `${c.replace(/_/g, " ")} ${n}`).join(" · ")})
          </div>
          <PickList title="Throw-away accounts to delete" rows={del} excluded={excluded} onToggle={toggle} testid="cleanup-accounts"
            label={a => <><b>{a.company || a.name}</b> · {a.role} · <span style={{ color: "#64748b" }}>{a.email || "no email"}</span></>}/>
          <PickList title="Demo logins (kept, but all their data removed)" rows={keep} excluded={excluded} onToggle={toggle} testid="cleanup-demo"
            label={a => <><b>{a.company || a.name}</b> · {a.email}</>}/>
          <PickList title="Jobs" rows={data.jobs} excluded={excluded} onToggle={toggle} testid="cleanup-jobs"
            label={j => <><b>{j.title}</b> · {j.id} · {j.status}</>}/>
          <PickList title="Design briefs" rows={data.briefs} excluded={excluded} onToggle={toggle} testid="cleanup-briefs"
            label={b => <><b>{b.title}</b> · {b.id} · {b.status}</>}/>
          <PickList title="Materials & finishing sub-RFQs" rows={data.rfqs} excluded={excluded} onToggle={toggle} testid="cleanup-rfqs"
            label={r => <><b>{(r.description || "").slice(0, 80)}</b> · {r.id} · {r.status}</>}/>
          <ZohoList refs={data.zoho_refs} testid="cleanup-zoho"/>
          <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
            <input data-testid="cleanup-phrase" value={phrase} onChange={e => setPhrase(e.target.value)} placeholder={`Type ${data.confirmation_phrase} to confirm`}
              style={{ border: "1px solid #CBD5E1", borderRadius: 7, padding: "8px 10px", fontSize: 12.5, minWidth: 260 }}/>
            <button className="primary-btn" data-testid="cleanup-purge-btn" disabled={busy || phrase.trim() !== data.confirmation_phrase} onClick={purge}
              style={{ background: "#B91C1C" }}>{busy ? "Removing…" : "Remove selected test data"}</button>
          </div>
        </>
      )}
      <FinancialReset onDone={load}/>
      <Batches batches={batches} onRestore={restore}/>
    </div>
  );
}
